import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStores } from "stores/setup/use-store";
import Step1BasicInfo from "../components/Step1BasicInfo";
import { Step2AlertTypeConditions } from "../components/Step2AlertTypeConditions";
import { Step3DeliveryConfig } from "../components/Step3DeliveryConfig";
import { useAlertCreationSubmission } from "./useAlertCreationSubmission";
import { useAlertFormData } from "./useAlertFormData";
import { useAlertGeospatial } from "./useAlertGeospatial";
import { useAlertTemplates } from "./useAlertTemplates";
import { useAlertCreationUsers } from "./useAlertCreationUsers";

interface UseAlertCreationDrawerParams {
  isOpen: boolean;
  onAlertCreatedSuccess?: () => void;
  showPhoneVerificationModal: boolean;
}

export function useAlertCreationDrawer({
  isOpen,
  onAlertCreatedSuccess,
  showPhoneVerificationModal
}: UseAlertCreationDrawerParams) {
  const {
    dataStores: { unitMeasurementStore, insightAlertStore },
  } = useStores();

  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });

  const formData = useAlertFormData();
  const templateType = insightAlertStore.createFormValues?.templateType;
  const templates = useAlertTemplates(templateType);
  const geospatial = useAlertGeospatial();
  const users = useAlertCreationUsers();
  const submission = useAlertCreationSubmission({
    onSuccessCallback: onAlertCreatedSuccess,
    showPhoneVerificationModal
  });

  useEffect(() => {
    if (isOpen) {
      unitMeasurementStore.getUserUnitMeasurementApi();
      insightAlertStore.setIsGeofenceSavedToLocation(false);
      users.loadAlertUsers(i18n?.language);
      // Always set to insight/event mode for alert creation (no more standalone alerts)
      insightAlertStore.setAlertCategory("insight");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unitMeasurementStore]);

  useEffect(() => {
    // Don't clear form data when drawer closes due to point/area selection mode
    // (user is navigating to Forecast Charts to select location)
    const isInSelectionMode =
      geospatial.isAreaDrawingMode || geospatial.isPointSelectionMode;

    // Don't clear if there's an edit operation in progress
    // This prevents the creation drawer cleanup from interfering with the edit flow
    // when both drawers are mounted on the same page
    const isEditOperationInProgress = !!insightAlertStore.operationRuleAlert;

    if (!isOpen && !isInSelectionMode && !isEditOperationInProgress) {
      formData.resetInitialValues();
      templates.resetTemplateSelection();
      geospatial.clearGeofence();
      users.clearUserTableSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const STEPS = useMemo(
    () => {
      // Base step for both modes: Step 1 title depends on alert category
      const step1Title = insightAlertStore.alertCategory === "standalone"
        ? t("step1DataAndLocation")
        : t("step1DataAndInsight");

      const steps: Array<{ title: string; component: React.ComponentType<any> }> = [
        {
          title: step1Title,
          component: Step1BasicInfo,
        },
      ];

      // For standalone alerts: Step 2 = Conditions, Step 3 = Preferences
      if (insightAlertStore.alertCategory === "standalone") {
        steps.push({
          title: t("step2Conditions"),
          component: Step2AlertTypeConditions,
        });
        steps.push({
          title: t("step3Preferences"),
          component: Step3DeliveryConfig,
        });
      } else {
        // For insight/event alerts: conditions come from the event (shown in Step 1)
        // so skip the conditions step and go directly to preferences
        steps.push({
          title: t("step3Preferences"),
          component: Step3DeliveryConfig,
        });
      }

      return steps;
    },
    [t, insightAlertStore.alertCategory]
  );

  return {
    t,
    ...formData,
    ...templates,
    ...geospatial,
    ...users,
    ...submission,
    STEPS,
  };
}
